import React, { useRef, useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Tooltip from '@mui/material/Tooltip';
import { suggestedQuestions } from '../details.tsx';

interface ChatMessagesProps {
  messages: string[];
}

const ChatMessages: React.FC<ChatMessagesProps> = ({ messages }) => {
  const endOfMessagesRef = useRef<HTMLDivElement>(null);
  const [randomQuestions, setRandomQuestions] = useState<string[]>([]);
  const [tooltipText, setTooltipText] = useState<string>('Copy');

  useEffect(() => {
    endOfMessagesRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const getRandomQuestions = (questions: string[], count: number) => {
      const shuffled = questions.sort(() => 0.5 - Math.random());
      return shuffled.slice(0, count);
    };

    setRandomQuestions(getRandomQuestions(suggestedQuestions, 3));
  }, []);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setTooltipText('Copied');
      setTimeout(() => setTooltipText('Copy'), 2000);
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        overflowY: 'auto',
        p: 1,
      }}
    > 
    { messages && messages.length > 0 ? (
      <Paper
        sx={{
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'transparent',
          height: 'fit-content',
        }}
      >
        <List sx={{ display: 'flex', flexDirection: 'column' }}>
          {messages.map((msg, index) => (
            <ListItem key={index} sx={{ display: 'flex', justifyContent: msg.startsWith('User:') ? 'flex-end' : 'flex-start' }}>
              {msg.startsWith('User:') ? (
                <Avatar sx={{ bgcolor: '#b3e5fc', marginRight: 1 }}>
                  <PersonIcon />
                </Avatar>
              ) : (
                <Avatar sx={{ bgcolor: '#c5e1a5', marginRight: 1 }}>
                  <SmartToyIcon />
                </Avatar>
              )}
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: msg.startsWith('User:') ? '#e1f5fe' : '#f1f8e9',
                  maxWidth: '80%',
                }}
              >
                <ListItemText
                  primary={msg.replace('User: ', '').replace('Bot: ', '')}
                  primaryTypographyProps={{ sx: { wordBreak: 'break-word', color: '#333' } }}
                />
              </Paper>
            </ListItem>
          ))}
        </List>
        <div ref={endOfMessagesRef} />
      </Paper>
    ) : (
      <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: 'gray.100', borderRadius: 2, boxShadow: 3 }}>
        <Typography variant="h5" component="h2" sx={{ mb: 4 }}>
          Suggested Questions
        </Typography>
        <Grid container spacing={2}>
          {randomQuestions.map((question, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card 
                sx={{ 
                  height: '100%', 
                  transition: 'background-color 0.3s', 
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.1)', 
                  }
                }}
              >
                <CardContent sx={{display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" color="textPrimary">
                    {question}
                  </Typography>

                  <Tooltip title={tooltipText} arrow>
                    <IconButton 
                      aria-label="copy" 
                      onClick={() => handleCopy(question)} 
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    )}
    </Box>
  );
};

export default ChatMessages;
